import { useState, type ReactNode } from 'react'
import { Icon } from '../ui/Icon'
import { Popover } from './Popover'
import { NotificationsPanel } from './NotificationsPanel'
import { useAttentionCount } from './attention.ts'
import { SettingsPanel } from './SettingsPanel'
import { ThemeToggle } from './ThemeToggle'

export interface TopBarProps {
  crumbs: string[]
  searchPlaceholder?: string
  onSearchFocus?: () => void
  /**
   * The signed-in account control, or absent. `App.tsx` supplies `<AccountMenu/>` only in the
   * authenticated tree (a base URL configured), so a console running against the mock renders
   * this bar exactly as it always has -- no MSAL, nothing that could throw for lack of a
   * provider above it.
   */
  accountSlot?: ReactNode
  /** The signed-in principal, for the settings panel. Absent on the demo path. */
  account?: { name?: string; username?: string }
}

/**
 * Ported from the Admin Console's TopBar. The search field is the Cmd+K command palette
 * trigger there; here it is inert and readOnly until there is something to search, which is
 * why it carries no handler by default rather than a box that swallows keystrokes.
 *
 * The bell and the cog were the same kind of placeholder and are no longer: both were bare
 * `<span>`s with no handler, which is a control that looks live and is not -- the one thing
 * `CONSOLES.md` §2.3 forbids in its own domain ("a gate that has not passed is never a
 * greyed-out button"), and the same principle applies to chrome. They are buttons now, they
 * open panels, and the panels report facts rather than inventing product.
 */
export function TopBar({
  crumbs,
  searchPlaceholder = 'Find a deployment or client...',
  onSearchFocus,
  accountSlot,
  account,
}: TopBarProps) {
  // One at a time. Two panels open at once overlap in the same corner, and closing the one
  // underneath is a guess about which the person meant.
  const [open, setOpen] = useState<'none' | 'notifications' | 'settings'>('none')
  const attention = useAttentionCount()

  const toggle = (which: 'notifications' | 'settings') => () =>
    setOpen((current) => (current === which ? 'none' : which))
  const close = () => setOpen('none')

  return (
    <header className="topbar">
      {/*
        A NAV, not a div. A breadcrumb trail is a set of links to ancestors of the current page,
        which is a navigation landmark -- `aria-label` distinguishes it from the sidebar's nav and
        the MAP document rail's, since a page with three unlabelled navs is three anonymous
        landmarks to anyone moving through the page by landmark. The class is unchanged, so this
        is the same box to CSS.
      */}
      <nav className="breadcrumb" aria-label="Breadcrumb">
        {/*
          Keyed by POSITION, not by text. A breadcrumb is positional -- the second crumb is the
          second crumb whatever it says -- and keying by the string made two crumbs with the
          same text collide. React then reused the wrong nodes across a navigation, so a stale
          crumb from the previous route survived into the next one: "Clients / Platform / Audit"
          on a page whose real trail is "Platform / Audit", and a run of six identical "Clients"
          after a few navigations. The duplicate text was crumbsFor's bug and is fixed; this is
          the reconciliation half, and it is worth keeping fixed independently.
        */}
        {crumbs.map((crumb, i) => (
          <span key={i}>
            <span className={`crumb ${i === crumbs.length - 1 ? 'active' : ''}`}>{crumb}</span>
            {i < crumbs.length - 1 && <span className="sep">/</span>}
          </span>
        ))}
      </nav>
      <div className="search" onClick={onSearchFocus}>
        <Icon name="search" size={15} />
        <input placeholder={searchPlaceholder} readOnly />
        <kbd>⌘K</kbd>
      </div>
      <div className="actions">
        {/*
          First in the group, before the two panel triggers. It owns no panel and never covers
          anything, so it does not want to be sandwiched between two controls that do.
        */}
        <ThemeToggle />

        <div className="action-wrap">
          <button
            type="button"
            className="icon-btn"
            data-popover-trigger
            aria-label={
              attention === null
                ? 'Notifications'
                : `Notifications, ${attention} item${attention === 1 ? '' : 's'} needing attention`
            }
            aria-expanded={open === 'notifications'}
            onClick={toggle('notifications')}
          >
            <Icon name="bell" size={17} />
            {/* No badge when the count is unknown. A 0 on a failed read is a lie in one character. */}
            {attention !== null && attention > 0 ? <span className="badge">{attention}</span> : null}
          </button>
          <Popover open={open === 'notifications'} onClose={close} title="Needs attention">
            <NotificationsPanel />
          </Popover>
        </div>

        <div className="action-wrap">
          <button
            type="button"
            className="icon-btn"
            data-popover-trigger
            aria-label="Settings"
            aria-expanded={open === 'settings'}
            onClick={toggle('settings')}
          >
            <Icon name="settings" size={17} />
          </button>
          <Popover open={open === 'settings'} onClose={close} title="This session">
            <SettingsPanel account={account} />
          </Popover>
        </div>

        {accountSlot}
      </div>
    </header>
  )
}
